import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function About() {
  return (
    <>
      <Navbar />

      <main>
        {/* Breadcrumb */}
        <section className="w-full bg-background-100 pt-24 pb-4 md:pt-28">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-400" aria-label="面包屑导航">
              <Link to="/" className="hover:text-foreground-600 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-600 font-medium">关于我们</span>
            </nav>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="w-full bg-background-100 pb-14 md:pb-16">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              关于牧教师
            </h1>
            <p className="text-foreground-500 text-base md:text-lg max-w-3xl leading-relaxed">
              蒙召牧养，装备成长的事奉平台
            </p>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <div className="rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20inviting%20church%20interior%20with%20wooden%20pews%20and%20sunlight%20streaming%20through%20windows%2C%20an%20open%20Bible%20on%20the%20pulpit%2C%20peaceful%20sacred%20space%2C%20reverent%20atmosphere%2C%20warm%20golden%20amber%20tones%2C%20minimalist%20composition&width=700&height=500&seq=about-mission-01&orientation=landscape"
                    alt="牧教师使命与异象"
                    className="w-full h-full object-cover"
                    title="牧教师 — 使命与异象"
                  />
                </div>
              </div>
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4 whitespace-nowrap">
                  我们的使命
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                  忠心服侍，扎根真道
                </h2>
                <div className="space-y-4 text-foreground-600 leading-relaxed">
                  <p>
                    牧教师诞生于一群长期在第一线事奉的牧者和教师中间。我们深知，每一位蒙召的仆人都在面对独特的挑战——每周按时站上讲台的压力、陪伴会众走过生命低谷的疲惫、以及自身灵命需要不断被更新的渴望。
                  </p>
                  <p>
                    我们的使命是成为牧者和教师的忠实伙伴，提供经过深思熟虑的讲道资源、经过实践检验的门训课程、以及帮助同工在事奉中保持健康的牧养指南。
                  </p>
                  <p>
                    我们相信，当牧者被好好地装备，整个教会都会因此蒙福。正如保罗嘱咐提摩太：「你当竭力在神面前得蒙喜悦，作无愧的工人，按着正意分解真理的道。」（提后2:15）
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                核心价值
              </h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
                我们所持守的信念，决定我们所走的方向
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: 'ri-book-2-line',
                  title: '扎根圣经',
                  desc: '所有内容都始于对神话语的深入研读和忠心分解，而非人的经验和潮流。',
                },
                {
                  icon: 'ri-hand-heart-line',
                  title: '牧养为本',
                  desc: '我们关注的不只是知识传递，更是生命的改变和群羊的真实需要。',
                },
                {
                  icon: 'ri-group-line',
                  title: '同工同行',
                  desc: '我们相信装备事奉者最好的方式是陪伴和同行，而非单向的专家讲座。',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '纯正持守',
                  desc: '在多元化的时代中，我们坚持纯正教义，忠于历代教会所传承的信仰。',
                },
              ].map((value) => (
                <div key={value.title} className="p-6 rounded-xl bg-background-50">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                    <i className={`${value.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {value.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation / Trust */}
        <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                同工见证
              </h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
                来自一线牧者和教师的真实反馈
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  name: '王弟兄',
                  role: '家庭教会牧者 · 江苏',
                  quote:
                    '在牧会十年后，我一度感到讲道资源枯竭。牧教师提供的系统解经系列让我重新找回了预备讲章的热情和深度。特别是「以基督为中心」的进路，彻底更新了我的讲台事奉。',
                },
                {
                  name: '李姊妹',
                  role: '主日学教师 · 浙江',
                  quote:
                    '门徒训练一直是我的负担，但始终缺乏系统性资源。牧教师的门训课程帮助我建立了一套完整的陪谈体系，现在我的小组中已经有三位姊妹开始带领别人了。',
                },
                {
                  name: '陈弟兄',
                  role: '青少年事工负责人 · 上海',
                  quote:
                    '「牧者自我关顾」那一系列对我来说是及时雨。在几乎耗竭的时候读到那些文章，让我意识到先照顾好自己的灵命不是自私，而是对呼召的忠心。',
                },
              ].map((testimonial) => (
                <div key={testimonial.name} className="p-6 rounded-xl bg-background-100">
                  <div className="flex items-center gap-1 text-accent-500 mb-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <blockquote className="text-sm text-foreground-600 leading-relaxed mb-4 italic">
                    "{testimonial.quote}"
                  </blockquote>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{testimonial.name}</p>
                    <p className="text-xs text-foreground-400">{testimonial.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">
              {/* Contact Info */}
              <div className="lg:col-span-2">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4 whitespace-nowrap">
                  联系我们
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                  期待你的来信
                </h2>
                <p className="text-foreground-500 text-sm leading-relaxed mb-8">
                  无论你对资源有建议、有投稿意愿、或是想了解更多关于牧教师的资讯，都欢迎通过以下方式与我们联系。
                </p>

                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50 text-foreground-500 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                      <p className="text-sm text-foreground-500">contact@mujiaoshi.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50 text-foreground-500 flex-shrink-0">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">微信公众号</p>
                      <p className="text-sm text-foreground-500">牧教师</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50 text-foreground-500 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">我们的祷告</p>
                      <p className="text-sm text-foreground-500">愿主使用这个平台，装备更多忠心仆人</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-3">
                <div className="p-6 md:p-8 rounded-xl bg-background-50">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">
                    发送信息
                  </h3>
                  <form
                    data-readdy-form
                    action="https://readdy.ai/api/form/d9sm6fd8o1vn02ekuhrg"
                    method="POST"
                    className="space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                          placeholder="请输入姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          电子邮箱
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                          placeholder="请输入邮箱"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                        placeholder="请输入主题"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        信息内容
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        required
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors resize-none"
                        placeholder="请输入信息内容（不超过500字）"
                      ></textarea>
                    </div>

                    {/* Honeypot */}
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />

                    <button
                      type="submit"
                      className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送信息
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                常见问题
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">关于牧教师的常见疑问</p>
            </div>

            <div className="space-y-4">
              {[
                {
                  q: '牧教师的资源是否免费？',
                  a: '是的，目前牧教师平台上的所有讲道资源、门训课程和牧养文章均为免费提供。我们相信「白白地得来，也要白白地舍去」（太10:8）。',
                },
                {
                  q: '我可以投稿或分享自己的资源吗？',
                  a: '非常欢迎！我们一直在寻找有牧养经验和解经深度的作者。请通过联系表单或邮件与我们沟通你的投稿意向。',
                },
                {
                  q: '文章是否允许转载或用于教会内部培训？',
                  a: '只要注明出处并保留原作者信息，欢迎用于教会内部培训和个人学习。如需商业用途，请先与我们联系。',
                },
                {
                  q: '内容是否有宗派立场？',
                  a: '我们持守使徒信经和尼西亚信经所宣告的历史性正统信仰。在非基要议题上，我们尊重不同宗派的传统和领受。',
                },
              ].map((faq, idx) => (
                <details key={idx} className="group rounded-xl bg-background-100 overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer text-foreground-900 font-medium text-sm md:text-base">
                    <span>{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-lg text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mujiaoshi.com/' },
              { '@type': 'ListItem', position: 2, name: '关于我们' },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '关于我们 — 牧教师',
            url: 'https://mujiaoshi.com/about',
            description: '了解牧教师的使命异象、核心价值和联系方式。',
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: '牧教师的资源是否免费？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '是的，目前牧教师平台上的所有讲道资源、门训课程和牧养文章均为免费提供。我们相信「白白地得来，也要白白地舍去」（太10:8）。',
                },
              },
              {
                '@type': 'Question',
                name: '我可以投稿或分享自己的资源吗？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '非常欢迎！我们一直在寻找有牧养经验和解经深度的作者。请通过联系表单或邮件与我们沟通你的投稿意向。',
                },
              },
              {
                '@type': 'Question',
                name: '文章是否允许转载或用于教会内部培训？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '只要注明出处并保留原作者信息，欢迎用于教会内部培训和个人学习。如需商业用途，请先与我们联系。',
                },
              },
              {
                '@type': 'Question',
                name: '内容是否有宗派立场？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '我们持守使徒信经和尼西亚信经所宣告的历史性正统信仰。在非基要议题上，我们尊重不同宗派的传统和领受。',
                },
              },
            ],
          }),
        }}
      />
    </>
  );
}